import React from 'react';
import { HomeOutlined, LinkOutlined, AudioOutlined, UserAddOutlined, MergeCellsOutlined, ExperimentOutlined, CommentOutlined } from '@ant-design/icons';
import { FctIcono } from '../../fct/react';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * Entradas del menú de GescomAux (raíl de GcCarcasa). Mismas entradas, mismo orden y mismo
 * filtrado por recurso ACL que tenía NavigationMenu (GCVSC-I001, R-05). Los textos no cambian.
 *
 * Iconos: del set del manual de marca digital (FctIcono) donde hay equivalente; si no lo hay,
 * @ant-design/icons en su variante outlined, en una tinta (R-14, acordado el 2026-10-03).
 */
export interface MenuItemConfig {
  key: string;
  icon: React.ReactNode;
  label: string;
  resource?: string; // Recurso ACL requerido; sin resource = siempre visible
  /** Grupo de segundo nivel (sección «Clientes», decisión de Javier 2026-10-04, spec rev. 2). El grupo
   *  se muestra si alguno de sus hijos es visible; no navega por sí mismo. */
  children?: MenuItemConfig[];
}

export const MENU_ITEMS: MenuItemConfig[] = [
  { key: '/', icon: <HomeOutlined />, label: 'Home' },
  { key: '/transmisiones', icon: <FctIcono nombre="transmision" />, label: 'Listado Gestión Transmisiones', resource: 'GCvsc.Transmisiones.Read' },
  { key: '/transmisiones/alta', icon: <FctIcono nombre="farmacia" />, label: 'Alta de transmisiones', resource: 'GCvsc.Transmisiones.Alta' },
  // Sección «Vinculación datos oficiales» (segundo nivel; decisión de Javier 2026-10-04): conciliación,
  // vinculación y mejora de CU2.
  {
    key: 'vinculacion-datos-oficiales',
    icon: <FctIcono nombre="documento-validado" />,
    label: 'Vinculación datos oficiales',
    children: [
      { key: '/reconciliacion', icon: <FctIcono nombre="informe" />, label: 'Conciliación con datos oficiales', resource: 'GCvsc.Reconciliacion.Read' },
      { key: '/vinculacion-cu2', icon: <LinkOutlined />, label: 'Vinculación CU2', resource: 'GCvsc.Reconciliacion.Read' },
      { key: '/mejora-cu2', icon: <FctIcono nombre="ajustes" />, label: 'Mejora CU2', resource: 'GCvsc.MejoraCU2.Read' },
    ],
  },
  { key: '/brevo-sync', icon: <FctIcono nombre="correo" />, label: 'Sincronización Brevo', resource: 'GCvsc.BrevoSync.Read' },
  // Sección «I+D» (segundo nivel; decisión de Javier 2026-10-04): KardioChat y el agente de actividad.
  {
    key: 'i-d',
    icon: <ExperimentOutlined />,
    label: 'I+D',
    children: [
      { key: '/chat', icon: <FctIcono nombre="mensaje" />, label: 'KardioChat', resource: 'GCvsc.KardioChat.Read' },
      { key: '/agent', icon: <AudioOutlined />, label: 'Registro agente', resource: 'GCvsc.Agente.Read' },
    ],
  },
  // Sección «Clientes» (segundo nivel; decisión de Javier 2026-10-04): agrupa el grid, las altas y
  // los duplicados. Cada hijo conserva su recurso ACL; el grupo aparece si alguno es visible.
  {
    key: 'clientes',
    icon: <FctIcono nombre="equipo" />,
    label: 'Clientes',
    children: [
      // F1.2 (GESCL-T04): grid de clientes con alta directa — recurso propio Clientes.Grid.Read,
      // que Marketing y Sistemas pueden tener con independencia del listado de altas.
      { key: '/clientes', icon: <FctIcono nombre="listado" />, label: 'Listado de clientes', resource: 'Clientes.Grid.Read' },
      // F1.4 (GESCL-T01): listado de gestión de altas — visible solo con el recurso ACL
      // Clientes.Altas.Read (deuda de T30 saldada; nomenclatura de dominio, D-GCVSC-013).
      { key: '/altas', icon: <UserAddOutlined />, label: 'Alta de clientes', resource: 'Clientes.Altas.Read' },
      // GESCL-T07 (tramo 5): herramienta de duplicados de cliente — mismo recurso que la gestión
      // de altas (D-GC-32). Primer uso de Clientes.Altas.Gestionar en el frontend.
      { key: '/clientes/duplicados', icon: <MergeCellsOutlined />, label: 'Duplicados', resource: 'Clientes.Altas.Gestionar' },
    ],
  },
  // Oportunidades (OPOR-I001): herramienta de Marketing; recurso único Oportunidades.Gestionar (R-14).
  { key: '/oportunidades', icon: <FctIcono nombre="cartera" />, label: 'Oportunidades', resource: 'Oportunidades.Gestionar' },
  // Seguimiento Documentación - Firma Digital: sin `resource` por el mismo motivo que
  // Altas — su recurso ACL aún no está dado de alta en el backend.
  { key: '/seguimiento-fdoc', icon: <FctIcono nombre="legal" />, label: 'Firma digital' },
  // Seguimiento de pactos (Pacto de Firma por WhatsApp): solo Sistemas, vía Pactos.Seguimiento.Read en la ACL.
  { key: '/seguimiento-pactos', icon: <FctIcono nombre="legal" />, label: 'Seguimiento de pactos', resource: 'Pactos.Seguimiento.Read' },
  // Encuestas CX (CXCP_ENCUESTAS): solo grupos CxCp (s) y Dpto. Sistemas (s), vía Cx.Panel.Read.
  // Sin equivalente en el set de marca: icono outlined (R-14).
  { key: '/encuestas-cx', icon: <CommentOutlined />, label: 'Encuestas CX', resource: 'Cx.Panel.Read' },
  // Ferias (07_MODULOS/FERIAS): herramienta de Marketing; recurso de Clientes Clientes.Altas.Gestionar (D-FE-12).
  { key: '/ferias', icon: <FctIcono nombre="calendario" />, label: 'Ferias', resource: 'Clientes.Altas.Gestionar' },
  // Empleados (07_MODULOS/EMPLEADOS): ficha profesional de los empleados, herramienta de Sistemas. Recurso Empleados.Ver.
  { key: '/empleados', icon: <FctIcono nombre="usuario" />, label: 'Empleados', resource: 'Empleados.Ver' },
  // Reportes (#393): el listado de los informes de Power BI que el usuario puede abrir. Recurso Reportes.Ver.
  { key: '/reportes', icon: <FctIcono nombre="informe" />, label: 'Reportes', resource: 'Reportes.Ver' },
];

/** Entradas visibles para el usuario actual: filtro por ACL por entrada; un grupo se muestra con los hijos visibles y solo si queda alguno. */
export function useMenuItems(): MenuItemConfig[] {
  const { hasPermission } = usePermissions();
  const visible = (item: MenuItemConfig) => !item.resource || hasPermission(item.resource);
  return MENU_ITEMS.flatMap((item) => {
    if (!item.children) return visible(item) ? [item] : [];
    const hijos = item.children.filter(visible);
    return hijos.length > 0 ? [{ ...item, children: hijos }] : [];
  });
}
